<template>
  <div class="st-auth-root">
    <div class="st-auth-card" style="max-width:420px;">
      <div class="text-center mb-4">
        <img src="assets/images/simi_trac_logo.png" alt="Simi Trac" style="height:50px;" />
        <h4 class="mt-3" style="color:#E8EDF4;font-weight:700;">Welcome Back</h4>
        <p style="color:#8BA4C4;font-size:14px;">Sign in to Simi Trac</p>
      </div>

      <!-- Tabs -->
      <div class="st-tabs mb-3">
        <button :class="['st-tab', mode === 'staff' ? 'active' : '']" @click="mode='staff'; error=''">Staff / Student</button>
        <button :class="['st-tab', mode === 'parent' ? 'active' : '']" @click="mode='parent'; error=''">Parent (OTP)</button>
      </div>

      <div class="st-alert st-alert-danger" v-if="error">{{ error }}</div>

      <!-- STAFF / STUDENT LOGIN -->
      <div v-if="mode === 'staff'">
        <div class="mb-3">
          <label class="st-label">Email or Phone</label>
          <input class="st-input" v-model="email" placeholder="admin@school.ac.ke or 0712345678" @keyup.enter="login" />
        </div>
        <div class="mb-3">
          <label class="st-label">Password</label>
          <div class="st-pw-wrap">
            <input :type="showPassword ? 'text' : 'password'" class="st-input st-pw-input" v-model="password" placeholder="Your password" @keyup.enter="login" />
            <button type="button" class="st-pw-toggle" @click="showPassword = !showPassword" :title="showPassword ? 'Hide password' : 'Show password'">
              <i class="mdi" :class="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"></i>
            </button>
          </div>
        </div>
        <div class="mb-3 st-remember">
          <label><input type="checkbox" v-model="rememberMe" /> <span>Remember me</span></label>
        </div>
        <button class="btn st-btn-primary w-100" @click="login" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
          {{ loading ? 'Signing in...' : 'Sign In' }}
        </button>
      </div>

      <!-- PARENT OTP LOGIN -->
      <div v-if="mode === 'parent' && otpStep === 1">
        <div class="mb-3">
          <label class="st-label">Phone Number</label>
          <input class="st-input" v-model="phone" placeholder="e.g. 0712345678" type="tel" @keyup.enter="requestOtp" />
        </div>
        <div class="mb-3 st-remember">
          <label><input type="checkbox" v-model="rememberMe" /> <span>Remember me on this device</span></label>
        </div>
        <p style="color:#8BA4C4;font-size:12px;">We'll send a login code via SMS to your phone.</p>
        <button class="btn st-btn-primary w-100" @click="requestOtp" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
          {{ loading ? 'Sending code...' : 'Send Login Code' }}
        </button>
      </div>

      <div v-if="mode === 'parent' && otpStep === 2">
        <p class="text-center" style="color:#10B981;font-size:14px;">✅ Code sent to {{ maskedPhone }}</p>
        <div class="mb-3">
          <label class="st-label">Enter Code</label>
          <input class="st-input text-center" v-model="otpCode" placeholder="Enter 6-digit code" maxlength="6" style="font-size:24px;letter-spacing:8px;" @keyup.enter="verifyOtp" />
        </div>
        <button class="btn st-btn-primary w-100" @click="verifyOtp" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
          {{ loading ? 'Verifying...' : 'Verify & Login' }}
        </button>
        <p class="text-center mt-2"><a href="javascript:void(0)" @click="otpStep=1; error=''" class="st-link" style="font-size:13px;">← Change number</a></p>
      </div>

      <p class="text-center mt-3" style="color:#8BA4C4;font-size:14px;">
        New school? <a href="javascript:void(0)" @click="$router.push('/register')" class="st-link">Register here</a>
      </p>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { executeJson, simitrack } from '@/api';

export default {
  name: 'Login',
  data() {
    return {
      mode: 'staff',
      email: '',
      password: '',
      phone: '',
      otpCode: '',
      otpStep: 1,
      maskedPhone: '',
      rememberMe: false,
      showPassword: false,
      error: '',
      loading: false
    };
  },
  async mounted() {
    // Honour ?mode=parent (or ?mode=staff) deep-link from the welcome SMS.
    // Hash-mode router puts query params after the hash route, so check both URL searches.
    try {
      const queryFromVue = this.$route && this.$route.query && this.$route.query.mode;
      const queryFromUrl = new URLSearchParams(window.location.search).get('mode');
      const m = queryFromVue || queryFromUrl;
      if (m === 'parent' || m === 'staff') this.mode = m;
    } catch (e) { /* router may not be present in some test envs */ }

    // Auto-login if Remember Me was ticked on the last successful login.
    // We DON'T trust the local cache — call getMe so the token is validated
    // against the server and we get fresh user / institution / allowedMenus.
    const savedToken = localStorage.getItem('authToken');
    const remembered = localStorage.getItem('rememberMe') === 'true';
    if (!savedToken || !remembered) return;

    // Prime sessionStorage so the api helper attaches the bearer header
    sessionStorage.setItem('token', savedToken);
    this.rememberMe = true;

    try {
      const r = await executeJson({ function: 'getMe' }, simitrack);
      if (r.data && r.data.success && r.data.data && r.data.data.user) {
        const d = r.data.data;
        // Build a synthetic login envelope so saveSession() does the full restore
        this.saveSession({
          token: savedToken,
          user: d.user,
          institution: d.institution || null,
          studentProfile: d.studentProfile || null,
          children: d.children || null,
          allowedMenus: d.allowedMenus || [],
        });
        const role = (d.user && d.user.role) || '';
        if (role === 'student') this.$router.push('/student-portal');
        else if (role === 'parent') this.$router.push('/my-children');
        else this.$router.push('/dashboard');
      } else {
        // Token expired or invalid — wipe saved state so we don't loop
        this.clearRemembered();
      }
    } catch (e) {
      this.clearRemembered();
    }
  },
  methods: {
    saveSession(d) {
      sessionStorage.setItem('isLoggedIn', 'true');
      sessionStorage.setItem('token', d.token);
      localStorage.setItem('authToken', d.token);
      sessionStorage.setItem('userId', d.user.id);
      sessionStorage.setItem('userName', d.user.name);
      sessionStorage.setItem('userRole', d.user.role);
      sessionStorage.setItem('userEmail', d.user.email || '');
      sessionStorage.setItem('userPhone', d.user.phone || '');
      if (d.institution) {
        sessionStorage.setItem('institutionId', d.institution.id);
        sessionStorage.setItem('institutionName', d.institution.name);
        sessionStorage.setItem('institutionSlug', d.institution.slug || '');
        sessionStorage.setItem('institutionType', d.institution.type || '');
      }
      if (d.studentProfile) sessionStorage.setItem('studentProfile', JSON.stringify(d.studentProfile));
      if (d.children) sessionStorage.setItem('children', JSON.stringify(d.children));
      sessionStorage.setItem('allowedMenus', JSON.stringify(d.allowedMenus || []));

      // The api helper (api/index.js) reads localStorage.authToken on EVERY
      // request — so it must be set regardless of Remember Me. The rememberMe
      // flag only controls whether mounted() auto-logs the user in next visit.
      localStorage.setItem('authToken', d.token);
      if (this.rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberMe');
      }
      // Legacy keys from earlier remember-me implementation — clean up if present
      localStorage.removeItem('savedRole');
      localStorage.removeItem('savedName');
      localStorage.removeItem('savedInstName');
      localStorage.removeItem('savedInstId');
      localStorage.removeItem('savedHasChildren');
    },
    clearRemembered() {
      // Wipes everything — used when an auto-login attempt fails (token invalid).
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('authToken');
      localStorage.removeItem('savedRole');
      localStorage.removeItem('savedName');
      localStorage.removeItem('savedInstName');
      localStorage.removeItem('savedInstId');
      localStorage.removeItem('savedHasChildren');
    },
    async login() {
      this.error = '';
      if (!this.email || !this.password) { this.error = 'Please enter email/phone and password'; return; }
      this.loading = true;
      try {
        const res = await executeJson({ function: 'login', email: this.email, password: this.password, deviceType: 'web' }, simitrack);
        const data = res.data;
        if (data.success) {
          const d = data.data;
          this.saveSession(d);

          const role = d.user.role;
          if (role === 'student') this.$router.push('/student-portal');
          else if (role === 'parent') this.$router.push('/my-children');
          else this.$router.push('/dashboard');
        } else {
          this.error = data.message || 'Invalid credentials';
        }
      } catch (e) {
        this.error = e?.response?.data?.message || 'Connection error. Please try again.';
      }
      this.loading = false;
    },

    async requestOtp() {
      this.error = '';
      const p = this.phone.trim();
      if (!p || p.length < 9) { this.error = 'Enter a valid phone number'; return; }
      this.loading = true;
      try {
        const res = await executeJson({ function: 'parentLoginRequest', phone: p }, simitrack);
        const data = res.data;
        if (data.success) {
          this.maskedPhone = data.data?.phone_masked || p.replace(/(\d{3})\d{4}(\d{3})/, '$1****$2');
          this.otpStep = 2;
        } else {
          this.error = data.message || 'Phone not found. Ask your school to register your number.';
        }
      } catch (e) {
        this.error = e?.response?.data?.message || 'Connection error. Please try again.';
      }
      this.loading = false;
    },

    async verifyOtp() {
      this.error = '';
      if (!this.otpCode || this.otpCode.length < 4) { this.error = 'Enter the code sent to your phone'; return; }
      this.loading = true;
      try {
        const res = await executeJson({ function: 'parentLoginVerify', phone: this.phone.trim(), otpCode: this.otpCode.trim() }, simitrack);
        const data = res.data;
        if (data.success) {
          const d = data.data;
          this.saveSession(d);
          this.$router.push('/my-children');
        } else {
          this.error = data.message || 'Invalid code. Try again.';
        }
      } catch (e) {
        this.error = e?.response?.data?.message || 'Connection error. Please try again.';
      }
      this.loading = false;
    }
  }
};
</script>

<style scoped>
.st-auth-root { background: #0A1628; min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 20px; }
.st-auth-card { background: #0F2847; border-radius: 16px; padding: 32px; width: 100%; box-shadow: 0 8px 32px rgba(0,0,0,.3); }
.st-label { color: #8BA4C4; font-size: 13px; margin-bottom: 4px; display: block; }
.st-input { width: 100%; background: #0A1628; border: 1px solid #1A3A6A; color: #E8EDF4; padding: 10px 14px; border-radius: 8px; font-size: 15px; box-sizing: border-box; }
.st-input:focus { border-color: #4FC3F7; outline: none; }
.st-input::placeholder { color: #4A6A8A; }
.st-btn-primary { background: #2E86DE; color: #fff; border: none; padding: 12px; border-radius: 8px; font-weight: 600; font-size: 15px; }
.st-btn-primary:hover { background: #2478C8; }
.st-btn-primary:disabled { opacity: 0.6; }
.st-link { color: #4FC3F7; text-decoration: none; }
.st-link:hover { text-decoration: underline; }
.st-alert-danger { background: rgba(239,68,68,.15); color: #EF4444; padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }

.st-tabs { display: flex; gap: 4px; background: #0A1628; border-radius: 8px; padding: 4px; }
.st-tab { flex: 1; background: transparent; border: none; color: #8BA4C4; padding: 10px; border-radius: 6px; font-size: 14px; font-weight: 500; cursor: pointer; transition: all .2s; }
.st-tab.active { background: #2E86DE; color: #fff; }
.st-tab:hover:not(.active) { color: #E8EDF4; }

.st-remember { display: flex; align-items: center; }
.st-remember label { display: flex; align-items: center; gap: 8px; color: #8BA4C4; font-size: 13px; cursor: pointer; }
.st-remember input[type="checkbox"] { width: 16px; height: 16px; accent-color: #2E86DE; cursor: pointer; }
.st-pw-wrap { position: relative; }
.st-pw-input { padding-right: 42px; }
.st-pw-toggle { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); background: transparent; border: none; color: #8BA4C4; cursor: pointer; padding: 6px 10px; font-size: 18px; line-height: 1; border-radius: 6px; transition: color .15s, background .15s; }
.st-pw-toggle:hover { color: #4FC3F7; background: rgba(79, 195, 247, 0.08); }
.st-pw-toggle:focus { outline: none; color: #4FC3F7; }
</style>